/**
 * 移动端（手机/平板）访问 PC 站时，自动跳转到同域的 wap 子域
 *
 *   例：a.com                     -> wap.a.com
 *      www.a.com                 -> wap.a.com              （www 会被去掉，避免 wap.www.a.com）
 *      a.com:8080                -> wap.a.com:8080         （端口保留）
 *      /verify?code=xxx          -> wap.a.com/g/verify?code=xxx
 *                                  （wap 站该页面部署在 /g 子目录，见 WAP_PATH_MAP）
 *
 * 设计：
 *   - 纯函数（isMobileDevice / isWapHost / resolveWapPath / toWapUrl / shouldRedirectToWap）不含副作用，可单测；
 *   - redirectMobileToWap() 负责读取浏览器环境并执行跳转（在 main.js 入口处调用）。
 *
 * 不跳转的情况（防止死循环 / 误跳）：
 *   1. 当前已经在 wap. / m. / mobile. 子域；
 *   2. localhost、127.0.0.1、IPv4/IPv6、内网无点域名（如 intranet）；
 *   3. 带 ?force_pc=1（强制留在 PC 版，并记住该选择）；
 *   4. 本地记录过用户选择 PC 版（localStorage[KEY_VIEW] === 'pc'）。
 *
 * 可选：?force_wap=1 强制跳转到 wap 站（用于测试/引导）。
 */

import Const from "./consts";

/** wap 子域前缀 */
export const WAP_PREFIX = "wap.";
/** 目标协议：'' = 跟随当前页面协议（https 页面跳 https，不降级）；如需强制可写 'http:' / 'https:' */
export const WAP_PROTOCOL = "";
/**
 * 路径映射：PC 站路径前缀 -> wap 站对应路径前缀
 *   wap 站部分页面部署在子目录时用它改写，例：/verify 在 wap 站上是 /g/verify
 *   force: true 表示该路径即使本地记录过"用户选择电脑版"也照样跳
 *              （验证码/注册这类链接在手机上必须能正常完成，所以强制跳）
 *   如需「整站都在 /g 下」，把 from 改成 '/'、to 改成 '/g/'（一行即可）
 */
export const WAP_PATH_MAP = [{ from: "/verify", to: "/g/verify", force: true }];
/** 强制留在 PC 版的 URL 参数（同时写入本地记忆） */
export const FORCE_PC_PARAM = "force_pc";
/** 强制跳转到 wap 站的 URL 参数（同时写入本地记忆） */
export const FORCE_WAP_PARAM = "force_wap";
/** 已经是移动站的主机前缀，命中则不跳（防死循环） */
const MOBILE_HOST_RE = /^(wap|m|mobile)\./i;
/** 移动端 UA 关键字（覆盖主流手机/平板/功能机浏览器） */
const MOBILE_UA_RE =
  /android|iphone|ipod|ipad|windows phone|windows mobile|iemobile|blackberry|bb10|playbook|opera mini|opera mobi|opr\/|kindle|silk|webos|palm|series60|symbian|midp|mobi|mobile/i;
/** 内网 / 本机主机名（不做跳转） */
const LOCAL_HOST_RE = /^(localhost|127(\.\d{1,3}){3}|0\.0\.0\.0|\[::1\]|.*\.local)$/i;
/** IPv4 字面量（不做跳转） */
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;


/**
 * 是否移动端设备
 * @param {string} ua navigator.userAgent
 * @param {{maxTouchPoints?: number}} [opts] iPadOS 13+ 的 UA 与 Mac 相同，需要触摸点辅助判断
 */
export function isMobileDevice(ua, opts) {
  const agent = String(ua || "");
  if (MOBILE_UA_RE.test(agent)) return true;
  // iPadOS 13+：UA 伪装成 Macintosh，但支持多点触控（真实 Mac 的 maxTouchPoints 为 0）
  const touchPoints = Number((opts && opts.maxTouchPoints) || 0);
  if (touchPoints > 1 && /Macintosh|Mac OS X/i.test(agent)) return true;
  return false;
}

/** 当前是否已经处于移动站（wap. / m. / mobile. 子域） */
export function isWapHost(hostname) {
  return MOBILE_HOST_RE.test(String(hostname || ""));
}

/** 该主机是否可跳转（排除本机 / IP / 无点内网域名） */
export function isRedirectableHost(hostname) {
  const host = String(hostname || "");
  if (!host || host.indexOf(".") === -1) return false;
  if (LOCAL_HOST_RE.test(host)) return false;
  if (IPV4_RE.test(host)) return false;
  return true;
}

/** 找出命中的路径映射规则（无则返回 null） */
function matchPathRule(pathname) {
  const path = pathname || "/";
  for (let i = 0; i < WAP_PATH_MAP.length; i += 1) {
    const rule = WAP_PATH_MAP[i];
    // 前缀命中：等于前缀本身，或以「前缀 + /」开头
    if (rule.from === "/" || path === rule.from || path.indexOf(rule.from + "/") === 0) {
      return rule;
    }
  }
  return null;
}

/**
 * 按 WAP_PATH_MAP 把 PC 站路径改写成 wap 站路径
 *   /verify          -> /g/verify
 *   /verify/x        -> /g/verify/x
 *   未命中规则       -> 原样返回（保持整站同路径，避免误改其它页面）
 * @param {string} pathname 当前页面路径（如 /verify）
 */
export function resolveWapPath(pathname) {
  const path = pathname || "/";
  const rule = matchPathRule(path);
  if (!rule) return path;
  const rest = rule.from === "/" ? path.slice(1) : path.slice(rule.from.length);
  const target = rule.to + rest;
  // 目标本身就带有前缀时不要重复叠加
  return target.charAt(0) === "/" ? target : "/" + target;
}

/** 该路径是否属于「强制跳 wap」的页面（用于忽略"用户选择电脑版"的记忆） */
export function isForcedWapPath(pathname) {
  const rule = matchPathRule(pathname);
  return !!(rule && rule.force);
}

/**
 * 由当前地址计算出 wap 站地址（保留协议、端口、路径、查询串、hash）
 * @param {{protocol:string,hostname:string,port?:string,pathname?:string,search?:string,hash?:string}} loc
 */
export function toWapUrl(loc) {
  const protocol = WAP_PROTOCOL || loc.protocol || "https:";
  const baseHost = String(loc.hostname || "").replace(/^www\./i, "");
  const port = loc.port ? ":" + loc.port : "";
  const pathname = resolveWapPath(loc.pathname);
  const search = loc.search || "";
  const hash = loc.hash || "";
  return `${protocol}//${WAP_PREFIX}${baseHost}${port}${pathname}${search}${hash}`;
}

/**
 * 纯判定：是否需要跳转到 wap 站
 * @param {{protocol:string,hostname:string,port?:string,pathname?:string,search?:string,hash?:string}} loc 当前地址
 * @param {string} ua 浏览器 UA
 * @param {{maxTouchPoints?:number, query?:object, savedView?:string}} [opts]
 *        query 当前查询参数（如 { force_pc: '1' }）；savedView 本地记忆（'pc' / 'wap'）
 * @returns {{redirect:boolean, url:string, reason:string, view?:string}}
 */
export function shouldRedirectToWap(loc, ua, opts) {
  const options = opts || {};
  const query = options.query || {};
  const savedView = options.savedView || "";
  const url = toWapUrl(loc);

  const forcePc = String(query[FORCE_PC_PARAM] || "") === "1";
  const forceWap = String(query[FORCE_WAP_PARAM] || "") === "1";

  // 强制参数优先（并记住用户选择）
  if (forcePc) return { redirect: false, url, reason: "force_pc", view: "pc" };
  if (forceWap) return { redirect: true, url, reason: "force_wap", view: "wap" };

  // 已经在移动站 / 不可跳转的主机
  if (isWapHost(loc.hostname)) return { redirect: false, url, reason: "already_wap" };
  if (!isRedirectableHost(loc.hostname)) return { redirect: false, url, reason: "host_not_redirectable" };

  // 用户之前手动选择过 PC 版（验证码/注册这类强制跳转页面除外）
  if (savedView === "pc" && !isForcedWapPath(loc.pathname)) {
    return { redirect: false, url, reason: "saved_pc" };
  }

  // 非移动端设备不跳
  if (!isMobileDevice(ua, options)) return { redirect: false, url, reason: "desktop" };

  return { redirect: true, url, reason: "mobile" };
}

/**
 * 浏览器环境：移动端访问 PC 站时跳转到 wap 子域
 * @returns {boolean} 是否已触发跳转（true 时调用方不要再挂载 PC 应用）
 */
export function redirectMobileToWap() {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  const { location } = window;
  if (!location || !location.hostname) return false;

  let savedView = "";
  try {
    savedView = window.localStorage.getItem(Const.KEY_VIEW) || "";
  } catch (e) {
    savedView = "";
  }

  let query = {};
  try {
    query = {};
    const params = new URLSearchParams(location.search || "");
    params.forEach((v, k) => {
      query[k] = v;
    });
  } catch (e) {
    query = {};
  }

  const result = shouldRedirectToWap(
    {
      protocol: location.protocol,
      hostname: location.hostname,
      port: location.port,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    },
    (window.navigator && window.navigator.userAgent) || "",
    {
      maxTouchPoints: window.navigator && window.navigator.maxTouchPoints,
      query,
      savedView,
    },
  );

  // 记录用户通过 URL 参数做出的选择
  if (result.view) {
    try {
      window.localStorage.setItem(Const.KEY_VIEW, result.view);
    } catch (e) {
      /* 隐私模式下忽略 */
    }
  }

  if (!result.redirect) return false;

  // 用 replace：不污染浏览器历史，用户点「返回」不会又被跳走
  window.location.replace(result.url);
  return true;
}

export default {
  WAP_PREFIX,
  WAP_PROTOCOL,
  WAP_PATH_MAP,
  FORCE_PC_PARAM,
  FORCE_WAP_PARAM,
  isMobileDevice,
  isWapHost,
  isRedirectableHost,
  resolveWapPath,
  isForcedWapPath,
  toWapUrl,
  shouldRedirectToWap,
  redirectMobileToWap,
};
